import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

function mergeOptions(options, rows) {
  const known = new Set(options.map((o) => o.label));
  const extra = [...new Set(rows.map((r) => r.value).filter((v) => v && !known.has(v)))].map((v) => ({ code: v, label: v }));
  return [...options, ...extra];
}

function RowsEditor({ label, rows, options, onChange, testIdPrefix }) {
  const addRow = () => onChange([...rows, { value: "", headcount: 1 }]);
  const updateRow = (i, key, val) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <Button type="button" size="sm" variant="outline" data-testid={`${testIdPrefix}-add-row`} onClick={addRow}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Row
        </Button>
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex gap-2 items-center">
            <SearchableSelect testId={`${testIdPrefix}-row-${i}-select`} className="flex-1" value={r.value || ""} onValueChange={(v) => updateRow(i, "value", v)} placeholder="Select"
  options={options.map((o) => ({ value: o.label, label: o.label, testId: `${testIdPrefix}-row-${i}-option-${o.code}` }))} />
            <Input
              type="number"
              min="0"
              className="w-24"
              data-testid={`${testIdPrefix}-row-${i}-headcount`}
              value={r.headcount}
              onChange={(e) => updateRow(i, "headcount", e.target.value)}
            />
            <button type="button" data-testid={`${testIdPrefix}-row-${i}-remove`} onClick={() => removeRow(i)} className="text-slate-400 hover:text-red-500">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-xs text-slate-400">No rows added.</p>}
      </div>
    </div>
  );
}

export default function ManpowerFormDialog({ entry, onClose, onOpenExisting, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!entry;
  const isSE = user?.role === "Site Engineer";
  const readOnly = isEdit && isSE && entry.locked;

  const [config, setConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [date, setDate] = useState(entry?.date ? entry.date.slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [companyStaff, setCompanyStaff] = useState((entry?.company_staff || []).map((r) => ({ value: r.designation, headcount: r.headcount })));
  const [contractLabour, setContractLabour] = useState((entry?.contract_labour || []).map((r) => ({ value: r.category, headcount: r.headcount })));
  const [remarks, setRemarks] = useState(entry?.remarks || "");

  useEffect(() => {
    const params = isEdit ? { site: entry.site } : {};
    client
      .get("/manpower-log/config", { params })
      .then((res) => setConfig(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoadingConfig(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkDuplicate = async (val) => {
    if (isEdit) return;
    try {
      const res = await client.get("/manpower-log/duplicate-check", { params: { date: val } });
      setDuplicate(res.data.exists ? res.data.id : null);
    } catch {
      setDuplicate(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      date: new Date(date).toISOString(),
      company_staff: companyStaff.filter((r) => r.value).map((r) => ({ value: r.value, headcount: parseInt(r.headcount, 10) || 0 })),
      contract_labour: contractLabour.filter((r) => r.value).map((r) => ({ value: r.value, headcount: parseInt(r.headcount, 10) || 0 })),
      remarks: remarks.trim() || null,
    };
    try {
      const res = isEdit ? await client.put(`/manpower-log/${entry.id}`, payload) : await client.post("/manpower-log", payload);
      toast.success(isEdit ? "Manpower Log updated" : "Manpower Log created");
      onSaved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manpower-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Manpower Log — ${config?.display_label || entry.site}` : "Add Manpower Log"}</DialogTitle>
        </DialogHeader>
        {loadingConfig ? (
          <p className="text-sm text-slate-400 py-8 text-center">Loading...</p>
        ) : readOnly ? (
          <div className="space-y-4">
            <p className="text-sm text-amber-600" data-testid="manpower-locked-message">
              This entry can only be edited on the day it was created. Contact a Manager/Director for corrections.
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase text-slate-400 font-mono mb-1">Company Staff</p>
                {companyStaff.map((r, i) => (
                  <p key={i}>
                    {r.value}: {r.headcount}
                  </p>
                ))}
              </div>
              <div>
                <p className="text-xs uppercase text-slate-400 font-mono mb-1">Contract Labour</p>
                {contractLabour.map((r, i) => (
                  <p key={i}>
                    {r.value}: {r.headcount}
                  </p>
                ))}
              </div>
            </div>
            {remarks && (
              <p className="text-sm">
                <span className="text-xs uppercase text-slate-400 font-mono block">Remarks</span>
                {remarks}
              </p>
            )}
            <DialogFooter>
              <Button type="button" onClick={onClose} data-testid="manpower-form-close-button">
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-xs text-slate-500">
              Site: <span className="font-medium text-slate-700">{config?.display_label}</span>
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Date</label>
              <Input
                type="date"
                data-testid="manpower-form-date-input"
                value={date}
                disabled={isEdit}
                onChange={(e) => {
                  setDate(e.target.value);
                  checkDuplicate(e.target.value);
                }}
              />
              {duplicate && (
                <p className="text-xs text-red-500 mt-1">
                  An entry already exists for this date.{" "}
                  <button type="button" className="underline" data-testid="manpower-open-duplicate-button" onClick={() => onOpenExisting(duplicate)}>
                    Open it to edit
                  </button>
                </p>
              )}
            </div>
            <RowsEditor
              label="Company Staff"
              rows={companyStaff}
              options={mergeOptions(config?.designation_options || [], companyStaff)}
              onChange={setCompanyStaff}
              testIdPrefix="manpower-company-staff"
            />
            <RowsEditor
              label="Contract Labour"
              rows={contractLabour}
              options={mergeOptions(config?.labour_category_options || [], contractLabour)}
              onChange={setContractLabour}
              testIdPrefix="manpower-contract-labour"
            />
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Remarks</label>
              <Textarea data-testid="manpower-form-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} data-testid="manpower-form-cancel-button">
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !!duplicate} data-testid="manpower-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
                {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Entry"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
